import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Routes from "models/routes";
import { GridToolbarContainer } from "@mui/x-data-grid";
import CommonButton from "components/common/ui/CommonButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Grid, InputAdornment, Tooltip } from "@mui/material";
import RecipeStepGridItemBox from "components/food/ui/recipe/RecipeStepGridItemBox";
import SpacedTextInput from "components/common/ui/SpacedTextInput";
import CircleIconButton from "components/common/ui/CircleIconButton";
import ClearIcon from "@mui/icons-material/Clear";
import SoftSquareIconButton from "components/common/ui/SoftSquareIconButton";
import SearchIcon from "@mui/icons-material/Search";

interface IngredientStockListTableToolbarProps {
  setKeywords: (keywords: string | undefined) => void;
}

const IngredientStockListTableToolbar: FC<
  IngredientStockListTableToolbarProps
> = ({ setKeywords }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse initial keywords from URL params
  const initialKeywords = useMemo(
    () => searchParams.get("keywords") || undefined,
    [searchParams],
  );

  const [displayKeywords, setDisplayKeywords] = useState<string | undefined>(
    initialKeywords,
  );

  // Sync local state with URL params when they change externally
  useEffect(() => {
    setDisplayKeywords(initialKeywords);
  }, [initialKeywords]);

  /**
   * Handle the keywords change
   * @param event
   */
  const handleKeywordsChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value ?? undefined;
    setDisplayKeywords(newValue);
  };

  /**
   * Handle click upload supplier order CSV button
   */
  const handleUploadPurchaseCSV = (): void => {
    navigate(Routes.INVENTORY_EDIT_CSV, {
      replace: false,
    });
  };

  /**
   * Handle click on search button
   */
  const handleSearchClick = useCallback(
    (searchingKeywords: string | undefined) => (): void => {
      setKeywords(searchingKeywords);
    },
    [setKeywords],
  );

  /**
   * Handle click on clear icon in searching bar
   */
  const handleClearClick = useCallback(() => {
    handleSearchClick(undefined)();
    setDisplayKeywords(undefined);
  }, [handleSearchClick]);

  /**
   * Handle keypress while focus on keyword field
   */
  const handleKeypress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        handleSearchClick(displayKeywords)();
      } else if (event.key === "Escape") {
        handleClearClick();
      }
    },
    [displayKeywords, handleClearClick, handleSearchClick],
  );

  return (
    <GridToolbarContainer>
      <Grid container spacing={1} columns={24}>
        <Grid item xs={24} sm={24} md={7} lg={8}>
          <CommonButton
            text="Supplier Order CSV"
            positive
            startIcon={<UploadFileIcon />}
            onClick={handleUploadPurchaseCSV}
          />
        </Grid>
        <Grid item xs={24} sm={24} md={17} lg={16}>
          <RecipeStepGridItemBox>
            <SpacedTextInput
              size="small"
              label="Keywords"
              InputProps={{
                endAdornment: displayKeywords ? (
                  <InputAdornment position="end">
                    <CircleIconButton
                      natural
                      sizepx={24}
                      onClick={handleClearClick}
                    >
                      <ClearIcon />
                    </CircleIconButton>
                  </InputAdornment>
                ) : null,
              }}
              onChange={handleKeywordsChange}
              onKeyDown={handleKeypress}
              value={displayKeywords ?? ""}
            />
            <Box>
              <Tooltip title="Search">
                <Box padding="3px 1rem 3px 0.5rem">
                  <SoftSquareIconButton
                    positive
                    sizepx={34}
                    onClick={handleSearchClick(displayKeywords)}
                  >
                    <SearchIcon />
                  </SoftSquareIconButton>
                </Box>
              </Tooltip>
            </Box>
          </RecipeStepGridItemBox>
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );
};

export default IngredientStockListTableToolbar;
